import React from 'react';
import { useApp } from '../context/AppContext';
import { Download, X, Rocket, Loader2, ShieldAlert, CheckCircle2, AlertTriangle, ExternalLink } from 'lucide-react';
import { triggerHaptic } from '../services/purrSynthesizer';
import { getApkDownloadUrl } from '../services/api';

export const UpdateBanner: React.FC = () => {
  const {
    isUpdateAvailable,
    updateDismissed,
    setUpdateDismissed,
    serverVersion,
    installUpdate,
    otaState,
    openOtaSettings,
    resetOtaState
  } = useApp();

  const isUpdatingActive = otaState.status !== 'idle';

  if ((!isUpdateAvailable || updateDismissed) && !isUpdatingActive) {
    return null;
  }

  if (!serverVersion && !isUpdatingActive) {
    return null;
  }

  const handleInstall = () => {
    triggerHaptic([30, 50, 30]);
    installUpdate();
  };

  const handleDismiss = () => {
    triggerHaptic(20);
    if (isUpdatingActive) {
      resetOtaState();
    }
    setUpdateDismissed(true);
  };

  const formattedDownloaded = (otaState.bytesDownloaded / (1024 * 1024)).toFixed(1);
  const formattedTotal = ((otaState.totalBytes || (serverVersion?.apkSizeBytes || 5500000)) / (1024 * 1024)).toFixed(1);

  return (
    <div className="relative z-30 bg-gradient-to-r from-amber-500/20 via-[#0d121c] to-amber-600/20 border-b border-fissal-gold/40 px-3.5 py-2.5 shadow-xl backdrop-blur-md animate-in slide-in-from-top-4 duration-200">
      {/* 1. DOWNLOADING STATE */}
      {otaState.status === 'downloading' && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="relative w-8 h-8 rounded-lg bg-amber-500/20 border border-fissal-gold/50 flex items-center justify-center shrink-0 shadow-quantum-glow">
                <Download className="w-4 h-4 text-fissal-gold animate-bounce" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-white tracking-wide">
                    Downloading Fissal Prime
                  </span>
                  <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-amber-500/20 text-fissal-gold border border-fissal-gold/30 font-semibold">
                    v{serverVersion?.version || '1.2.0'}
                  </span>
                </div>
                <p className="text-[10px] text-slate-400 font-mono mt-0.5">
                  {otaState.percent}% • {formattedDownloaded} MB / {formattedTotal} MB
                </p>
              </div>
            </div>

            <button
              onClick={handleDismiss}
              className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-slate-200 transition-colors"
              title="Cancel update"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-slate-900/80 rounded-full h-2 overflow-hidden border border-white/10 p-[1px]">
            <div
              className="bg-gradient-to-r from-amber-500 via-amber-400 to-emerald-400 h-full rounded-full transition-all duration-150 shadow-[0_0_12px_rgba(245,158,11,0.5)]"
              style={{ width: `${Math.max(4, otaState.percent)}%` }}
            />
          </div>
        </div>
      )}

      {/* 2. PREPARING INSTALLER STATE */}
      {otaState.status === 'preparing' && (
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center shrink-0">
              <Loader2 className="w-4 h-4 text-emerald-400 animate-spin" />
            </div>
            <div>
              <span className="text-xs font-bold text-white">Preparing Installer Package...</span>
              <p className="text-[10px] text-emerald-400/80">Launching native package updater</p>
            </div>
          </div>
        </div>
      )}

      {/* 3. PERMISSION REQUIRED STATE */}
      {otaState.status === 'permission_required' && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-amber-500/20 border border-amber-500/50 flex items-center justify-center shrink-0">
                <ShieldAlert className="w-4 h-4 text-amber-400" />
              </div>
              <div>
                <span className="text-xs font-bold text-white">Install Permission Needed</span>
                <p className="text-[10px] text-amber-200/90">Allow Fissal Prime to install unknown apps</p>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={openOtaSettings}
                className="px-2.5 py-1 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs shadow-quantum-glow transition-all active:scale-95"
              >
                Settings
              </button>
              <button
                onClick={handleInstall}
                className="px-2.5 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs transition-all active:scale-95"
              >
                Retry
              </button>
              <button
                onClick={handleDismiss}
                className="p-1 rounded bg-white/5 hover:bg-white/10 text-slate-400"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 4. READY STATE */}
      {otaState.status === 'ready' && (
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center shrink-0 shadow-quantum-glow">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            </div>
            <div>
              <span className="text-xs font-bold text-white">Update Ready!</span>
              <p className="text-[10px] text-emerald-400/90">Package downloaded · Tap Install Now to update.</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={handleInstall}
              className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 text-slate-950 font-bold text-xs flex items-center gap-1.5 shadow-quantum-glow transition-all active:scale-95"
            >
              <Download className="w-3.5 h-3.5 stroke-[2.5]" />
              <span>Install Now</span>
            </button>
            <button
              onClick={resetOtaState}
              className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400"
              title="Dismiss"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* 5. ERROR STATE */}
      {otaState.status === 'error' && (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-rose-500/20 border border-rose-500/50 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-4 h-4 text-rose-400" />
              </div>
              <div className="min-w-0">
                <span className="text-xs font-bold text-rose-300">Update Failed</span>
                <p className="text-[10px] text-slate-300 truncate">{otaState.error || 'Network error downloading APK.'}</p>
              </div>
            </div>

            <div className="flex items-center gap-1.5 shrink-0">
              <button
                onClick={handleInstall}
                className="px-2.5 py-1 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs shadow-quantum-glow"
              >
                Retry
              </button>
              <a
                href={getApkDownloadUrl()}
                target="_blank"
                rel="noreferrer"
                className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs flex items-center gap-1 border border-white/10"
              >
                <ExternalLink className="w-3 h-3" /> Browser
              </a>
              <button
                onClick={resetOtaState}
                className="p-1.5 rounded-lg bg-white/5 text-slate-400 hover:text-slate-200"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 6. IDLE (UPDATE AVAILABLE) DEFAULT STATE */}
      {otaState.status === 'idle' && serverVersion && (
        <div className="flex items-center justify-between gap-3">
          {/* Left: Icon & Description */}
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <div className="relative w-8 h-8 rounded-lg bg-amber-500/20 border border-fissal-gold/50 flex items-center justify-center shrink-0 shadow-quantum-glow">
              <Rocket className="w-4 h-4 text-fissal-gold animate-bounce" />
              <div className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-xs font-bold text-white tracking-wide">
                  Update Available
                </span>
                <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-amber-500/20 text-fissal-gold border border-fissal-gold/30 font-semibold">
                  v{serverVersion.version}
                </span>
                {serverVersion.apkSizeFormatted && (
                  <span className="text-[9px] font-mono text-slate-400">
                    ({serverVersion.apkSizeFormatted})
                  </span>
                )}
              </div>
              <p className="text-[10px] text-slate-300 truncate mt-0.5">
                {serverVersion.releaseNotes || 'New sovereign features and stability enhancements.'}
              </p>
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={handleInstall}
              className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold text-xs flex items-center gap-1.5 shadow-quantum-glow transition-all active:scale-95"
              title="Download and prepare update directly in-app"
            >
              <Download className="w-3.5 h-3.5 stroke-[2.5]" />
              <span>Update Now</span>
            </button>

            <button
              onClick={handleDismiss}
              className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-slate-200 transition-colors"
              title="Dismiss update notice"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
